import React, { useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import {
  Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import { Icon, T } from '../PartnerSignups/onboardingDesign'
import { Badge, GhostButton, LinkButton, Notice, OutlineButton } from '../PartnerSignups/onboardingUi'
import { day, headCell, tableSx } from './PortalParts'

/**
 * Super admin: the restaurants credited to an agent login (vendorId) or to an
 * ISO (isoId), earning now first, then the ones that ended: how each came in
 * (signed up through them, or assigned by hand) and an Unassign that stops it
 * from now on (backend utils/restaurantCredit.js).
 */

const LIST = gql`
  query CreditedRestaurants($vendorId: ID, $isoId: ID) {
    creditedRestaurants(vendorId: $vendorId, isoId: $isoId) {
      linkId restaurantId restaurantName agentId agentName source assignedByName since endedAt
    }
  }
`
const END = gql`mutation EndRestaurantCreditFromList($restaurantId: ID!) { endRestaurantCredit(restaurantId: $restaurantId) { restaurantId kind } }`

export default function CreditedRestaurantsDialog({ name, vendorId = null, isoId = null, onClose, onChanged, onAssign }) {
  const { data, loading, error, refetch } = useQuery(LIST, { variables: { vendorId, isoId }, fetchPolicy: 'network-only' })
  const [end, { loading: ending }] = useMutation(END)
  const [confirming, setConfirming] = useState(null)
  const [actionError, setActionError] = useState(null)
  const rows = (data && data.creditedRestaurants) || []
  const active = rows.filter(r => !r.endedAt)

  const unassign = async (r) => {
    setActionError(null)
    try {
      await end({ variables: { restaurantId: r.restaurantId } })
      setConfirming(null)
      await refetch()
      if (onChanged) onChanged()
    } catch (e) {
      setActionError(e.message)
    }
  }

  return (
    <Dialog open onClose={ending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {name}’s restaurants
        <Typography sx={{ color: T.muted, fontSize: 14 }}>
          {active.length} earning now · {active.filter(r => r.source !== 'ASSIGNED').length} signed up · {active.filter(r => r.source === 'ASSIGNED').length} assigned by hand
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: T.divider, p: 0 }}>
        {actionError && <Notice severity="error" sx={{ m: 2 }} onClose={() => setActionError(null)}>{actionError}</Notice>}
        {loading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box>
          : error ? <Notice severity="error" sx={{ m: 2 }}>{error.message}</Notice>
            : rows.length === 0 ? <Typography sx={{ p: 5, textAlign: 'center', color: T.hint }}>No restaurants yet.</Typography>
              : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: isoId ? 760 : 640, ...tableSx }}>
                    <TableHead>
                      <TableRow>
                        {['Restaurant', ...(isoId ? ['Agent'] : []), 'How', 'Since', ''].map(h => <TableCell key={h} sx={headCell}>{h}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(r => (
                        <TableRow key={r.linkId} sx={{ opacity: r.endedAt ? 0.6 : 1 }}>
                          <TableCell sx={{ fontWeight: 600 }}>{r.restaurantName}</TableCell>
                          {isoId && <TableCell sx={{ color: r.agentName ? T.text : T.hint }}>{r.agentName || 'The ISO'}</TableCell>}
                          <TableCell>
                            {r.source === 'ASSIGNED'
                              ? <Badge kind="info">Assigned{r.assignedByName ? ` by ${r.assignedByName}` : ''}</Badge>
                              : <Badge kind="green">Signed up</Badge>}
                          </TableCell>
                          <TableCell sx={{ color: T.text2, whiteSpace: 'nowrap' }}>{day(r.since)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {r.endedAt ? (
                              <Typography sx={{ color: T.hint, fontSize: 13 }}>Ended {day(r.endedAt)}</Typography>
                            ) : confirming === r.linkId ? (
                              <>
                                <LinkButton size="small" disabled={ending} onClick={() => unassign(r)}>Yes, unassign</LinkButton>
                                <LinkButton size="small" disabled={ending} onClick={() => setConfirming(null)} sx={{ color: T.muted }}>Keep</LinkButton>
                              </>
                            ) : (
                              <LinkButton size="small" onClick={() => setConfirming(r.linkId)}>Unassign</LinkButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
        {confirming && (
          <Typography sx={{ color: T.text2, fontSize: 13.5, px: { xs: 2, sm: 3 }, py: 1.5, borderTop: `1px solid ${T.divider}` }}>
            Unassigning stops them earning on its orders from now on. What they already earned stays theirs.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        {onAssign && (
          <OutlineButton onClick={onAssign} sx={{ mr: 'auto' }}>
            <Icon.Store size={16} />
            Assign restaurants to {name}
          </OutlineButton>
        )}
        <GhostButton onClick={onClose}>Close</GhostButton>
      </DialogActions>
    </Dialog>
  )
}
